import { Box, IconButton, Paper } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  ConnectDragPreview,
  ConnectDragSource,
  ConnectDropTarget,
  DragSource,
  DragSourceConnector,
  DragSourceMonitor,
  DropTarget,
  DropTargetConnector,
  DropTargetMonitor,
} from "react-dnd";
import { FieldArrayRenderProps } from "react-final-form-arrays";

import { IWidget } from "services/KnowfullyAdminClient";
import { DraggableInstance, DragItem, shouldMove } from "utils/dndUtils";

type DraggableWidgetProps = {
  fieldName: string;
  index: number;
  widget: IWidget;
  onMoveWidget: (fromIndex: number, toIndex: number) => void;
  onRemoveWidget: (index: number) => void;

  // Supplied by react-dnd HOCs
  isDragging: boolean;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  connectDragPreview: ConnectDragPreview;
};

const DraggableWidget = forwardRef<HTMLDivElement, DraggableWidgetProps>(
  (
    {
      index,
      widget,
      onRemoveWidget,
      isDragging,
      connectDragSource,
      connectDragPreview,
      connectDropTarget,
    },
    ref
  ) => {
    const elementRef = useRef(null);
    connectDragSource(elementRef);
    connectDropTarget(elementRef);
    connectDragPreview(connectDropTarget(elementRef));

    useImperativeHandle<any, DraggableInstance>(ref, () => ({
      getNode: () => elementRef.current,
    }));

    const opacity = isDragging ? 0 : 1;

    return (
      <Box mb={2}>
        <div ref={elementRef} style={{ opacity }}>
          <Paper elevation={3} square>
            <Box p={1} display="flex" alignItems="center">
              {connectDragSource(
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "move",
                    marginRight: 8,
                  }}
                >
                  <DragHandleIcon />
                </div>
              )}

              <Box flexGrow={1}>
                <div>{widget.name}</div>
              </Box>

              <IconButton onClick={() => onRemoveWidget(index)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
        </div>
      </Box>
    );
  }
);

const EnabledDraggableWidget = DropTarget(
  "WIDGET",
  {
    hover(
      { index, onMoveWidget }: DraggableWidgetProps,
      monitor: DropTargetMonitor,
      component: DraggableInstance
    ) {
      if (!component) {
        return null;
      }

      // node = HTML Div element from imperative API
      const node = component.getNode();
      if (!node) {
        return null;
      }

      const item = monitor.getItem<DragItem>();
      const dragIndex = item.index;
      const hoverIndex = index;

      if (shouldMove(hoverIndex, node, dragIndex, monitor)) {
        onMoveWidget(item.index, index);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex;
      }
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  })
)(
  DragSource(
    "WIDGET",
    {
      beginDrag: ({ widget, index }: DraggableWidgetProps) => ({
        id: widget.id,
        index,
      }),
    },
    (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
      connectDragSource: connect.dragSource(),
      connectDragPreview: connect.dragPreview(),
      isDragging: monitor.isDragging(),
    })
  )(DraggableWidget)
);

type OrderableWidgetsProps = {
  fields: FieldArrayRenderProps<IWidget, HTMLElement>["fields"];
};

const OrderableWidgets = ({ fields }: OrderableWidgetsProps) => {
  return (
    <div>
      {fields.map((name, i) => {
        const widget = fields.value[i];
        return (
          <EnabledDraggableWidget
            key={i}
            widget={widget}
            fieldName={name}
            index={i}
            onMoveWidget={fields.move}
            onRemoveWidget={fields.remove}
          />
        );
      })}
    </div>
  );
};

export default OrderableWidgets;
